import { useEffect, useState } from "react";
import { Apple, Monitor, Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API, errMsg, copyText } from "@/lib/api";

const Field = ({ label, placeholder, value, onChange, testid }) => (
  <label className="grid grid-cols-[130px_1fr] items-center gap-3 text-sm">
    <span className="text-slate-600">{label}</span>
    <Input data-testid={testid} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
  </label>
);

const GenerateForm = () => {
  const [form, setForm] = useState({ name: "", company: "", site: "", department: "", device_type: "" });
  const [binaries, setBinaries] = useState(false);
  const [busy, setBusy] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/agent/meta").then(({ data }) => setBinaries(data.binaries)); }, []);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post("/agent/profiles", form);
      window.location.href = `${API}/agent/installer/${kind}?key=${encodeURIComponent(data.token)}`;
      toast.success("Installateur généré");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTimeout(() => setBusy(""), 1500);
    }
  };

  const copyLink = async (kind) => {
    try {
      const { data } = await api.post("/agent/profiles", form);
      const ok = await copyText(`${API}/agent/installer/${kind}?key=${encodeURIComponent(data.token)}`);
      if (ok) toast.success("Lien copié — envoyez-le au client");
      else toast.error("Copie impossible : réessayez en gardant la fenêtre active");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="generate-form">
      <p className="text-sm text-slate-600">Étiquetez la machine : ces informations s'afficheront dans Access dès l'installation. Tous les champs sont facultatifs.</p>
      <div className="space-y-3">
        <Field label="Nom" placeholder="Nom de la machine si vide" value={form.name} onChange={set("name")} testid="gen-name-input" />
        <Field label="Entreprise" placeholder="Rangé dans ce groupe" value={form.company} onChange={set("company")} testid="gen-company-input" />
        <Field label="Site" placeholder="ex. Siège, Agence Nord" value={form.site} onChange={set("site")} testid="gen-site-input" />
        <Field label="Département" placeholder="ex. Comptabilité" value={form.department} onChange={set("department")} testid="gen-department-input" />
        <Field label="Type d'appareil" placeholder="ex. Portable, Serveur" value={form.device_type} onChange={set("device_type")} testid="gen-devicetype-input" />
      </div>
      {!binaries ? (
        <p className="text-sm bg-amber-50 text-amber-800 rounded-lg p-3" data-testid="exe-pending-note">Les installateurs se préparent (compilation GitHub). Revenez ici une fois terminée.</p>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-3 pt-1">
            <div className="space-y-1.5">
              <Button className="h-11 w-full" disabled={busy} onClick={() => download("windows-exe")} data-testid="download-windows-exe">
                {busy === "windows-exe" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Monitor className="h-4 w-4 mr-2" />} Windows (.exe)
              </Button>
              <button type="button" onClick={() => copyLink("windows-exe")} className="w-full text-xs text-blue-600 hover:underline" data-testid="copy-link-windows">Copier le lien à envoyer</button>
            </div>
            <div className="space-y-1.5">
              <Button className="h-11 w-full" variant="secondary" disabled={busy} onClick={() => download("mac-pkg")} data-testid="download-mac-pkg">
                {busy === "mac-pkg" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Apple className="h-4 w-4 mr-2" />} macOS (.pkg)
              </Button>
              <button type="button" onClick={() => copyLink("mac-pkg")} className="w-full text-xs text-blue-600 hover:underline" data-testid="copy-link-mac">Copier le lien à envoyer</button>
            </div>
          </div>
          <p className="text-xs text-slate-600">Le client double-clique sur le fichier : l'agent s'installe seul, démarre avec l'ordinateur et apparaît dans Access. Vous pouvez aussi lui envoyer le lien pour qu'il télécharge lui-même.</p>
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-slate-400" /> Au premier lancement, un avertissement « fichier non signé » peut apparaître : « Informations complémentaires » puis « Exécuter quand même ».
          </p>
        </>
      )}
    </div>
  );
};

export const GenerateDialog = ({ trigger }) => (
  <Dialog>
    <DialogTrigger asChild>
      {trigger || <Button data-testid="generate-installer-btn" className="w-full h-11 text-[16px] font-semibold bg-[#2563EB] hover:bg-[#1D4ED8] rounded">Générer +</Button>}
    </DialogTrigger>
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="generate-dialog">
      <DialogHeader><DialogTitle>Générer le programme d'installation</DialogTitle></DialogHeader>
      <GenerateForm />
    </DialogContent>
  </Dialog>
);
